import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(null);
  const [filters, setFilters] = useState({ search: '', level: '', type: '' });
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.level) params.set('level', filters.level);
      if (filters.type) params.set('type', filters.type);
      const res = await api.get(`/jobs?${params}`);
      setJobs(res.data.jobs || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, []);

  const handleMatch = async (jobId) => {
    setMatching(jobId);
    try {
      await api.post(`/match/job/${jobId}`);
      toast.success('Analysis complete! Check History.');
      navigate('/history');
    } catch (err) {
      if (err.response?.status === 404 && err.response?.data?.message?.includes('resume')) {
        toast.error('Upload your resume first!');
        navigate('/upload');
      } else {
        toast.error(err.response?.data?.message || 'Match failed');
      }
    } finally {
      setMatching(null);
    }
  };

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ fontFamily: 'Syne', fontSize: 26, fontWeight: 800, marginBottom: 8 }}>💼 Job Board</div>
          <p style={{ color: 'var(--muted)', fontSize: 14 }}>Browse jobs and instantly match them with your resume.</p>
        </div>
        {user?.role === 'recruiter' && (
          <button className="btn btn-primary">+ Post a Job</button>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <input className="input" style={{ flex: 2, minWidth: 200, marginBottom: 0 }} placeholder="Search jobs..." value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })} />
          <select className="input" style={{ flex: 1, minWidth: 140, marginBottom: 0 }} value={filters.level}
            onChange={e => setFilters({ ...filters, level: e.target.value })}>
            <option value="">All Levels</option>
            <option value="junior">Junior</option>
            <option value="mid">Mid</option>
            <option value="senior">Senior</option>
            <option value="lead">Lead</option>
          </select>
          <select className="input" style={{ flex: 1, minWidth: 140, marginBottom: 0 }} value={filters.type}
            onChange={e => setFilters({ ...filters, type: e.target.value })}>
            <option value="">All Types</option>
            <option value="full-time">Full Time</option>
            <option value="part-time">Part Time</option>
            <option value="contract">Contract</option>
            <option value="internship">Internship</option>
          </select>
          <button className="btn btn-primary" onClick={fetchJobs}>Search</button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>Loading jobs...</p>
      ) : jobs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 32px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔎</div>
          <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No jobs found</div>
          <p style={{ color: 'var(--muted)', fontSize: 14 }}>Try different filters or check back later.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {jobs.map(job => (
            <div className="card" key={job._id} style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <div style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 700 }}>{job.title}</div>
                    <span className="badge badge-purple">{job.experienceLevel}</span>
                    <span className="badge badge-green">{job.type}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                    {job.company}{job.location && ` · ${job.location}`}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 14 }}>
                    {job.description.substring(0, 200)}...
                  </p>
                  {job.skills?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {job.skills.slice(0, 6).map(s => <span key={s} className="tag tag-purple">{s}</span>)}
                    </div>
                  )}
                </div>
                <button className="btn btn-primary" disabled={matching === job._id}
                  onClick={() => handleMatch(job._id)} style={{ flexShrink: 0 }}>
                  {matching === job._id ? '⏳...' : '🎯 Match Me'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
